import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { authenticatedFetch } from '../../../utils/auth';
import SearchInput from '../../../components/SearchInput';
import { IconCheck, IconDownload, IconFileText, IconSettings, IconTrash } from '@tabler/icons-react';
import AdminEditPrihlaskaModal from './AdminEditPrihlaskaModal';

interface Person {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    address: string;
    houseNumber?: string;
    city?: string;
    psc?: string;
    rodCislo?: string;
    isBernolakova: boolean;
    schoolName?: string;
    gender?: 'MALE' | 'FEMALE';
    type: 'PARENT' | 'ADULT' | 'KID';
    schoolyearId?: string;
    schoolyear?: {
        id: string;
        name: string;
        order: number;
        type: 'PRESCHOOL' | 'PRIMARY' | 'HIGHSCHOOL' | 'UNIVERSITY';
    };
}

interface User {
    id: string;
    email: string;
}

interface Kruzok {
    id: string;
    name: string;
    description?: string;
    price: number;
    capacity: number;
    currentRegistrations: number;
    time: string;
    surcharge: boolean;
    isStretko: boolean;
    gender: 'MALE' | 'FEMALE' | 'BOTH';
    type: 'TRIH' | 'BERN';
    requiresZapisne: boolean;
    schoolYear?: Array<{
        id: string;
        name: string;
        order: number;
        type: 'PRESCHOOL' | 'PRIMARY' | 'HIGHSCHOOL' | 'UNIVERSITY';
    }>;
}

interface AdminSubmission {
    id: string;
    odovzdalRO?: boolean;
    reduction?: number;
    odovzdalVP?: boolean;
    odovzdalCP?: boolean;
    submitionDate?: string;
}

interface Prihlaska {
    id: string;
    personId: string;
    status: 'DRAFT' | 'GENERATED' | 'REJECTED' | 'CANCELLED' | 'ACCEPTED';
    isVP: boolean;
    isCp: boolean;
    paymentOption: 'CASH' | 'EDUPAGE';
    toPay: number;
    adminSubmissionDate?: string;
    createdAt: string;
    updatedAt: string;
    hash: string;
    person: Person;
    user: User;
    kruzky: Kruzok[];
    adminsubmition: AdminSubmission[];
    generatedAt: string; // New field for when status was set to GENERATED
}

interface PrihlaskyResponse {
    data: Prihlaska[];
    total: number;
    page: number;
    totalPages: number;
}

const Prihlasky: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedPrihlaskaId = searchParams.get('prihlaskaId');
    const [allPrihlasky, setAllPrihlasky] = useState<Prihlaska[]>([]); // All data from API
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [calculatedPrices, setCalculatedPrices] = useState<Record<string, number>>({}); // Store calculated prices by prihlaska ID
    const [acceptedTotals, setAcceptedTotals] = useState<any>(null);
    const [schoolYears, setSchoolYears] = useState<Array<{
        id: string;
        name: string;
        order: number;
        type: 'PRESCHOOL' | 'PRIMARY' | 'HIGHSCHOOL' | 'UNIVERSITY';
    }>>([]);
    const [filters, setFilters] = useState({
        status: '',
        search: '',
        page: 1,
        limit: 20,
        showOldOnly: false,
        personType: '',
        paymentOption: '',
        schoolyearType: [] as string[],
        hasVP: '',
        hasCP: '',
        submittedVP: '',
        submittedCP: '',
        submittedRO: '',
        kruzokType: '',
        gender: '',
        schoolyearId: [] as string[],
        dateFrom: '',
        dateTo: '',
        submissionDateFrom: '',
        submissionDateTo: '',
        priceFrom: '',
        priceTo: ''
    });
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [selectedPrihlaska, setSelectedPrihlaska] = useState<Prihlaska | null>(null);
    const [showSubmissionModal, setShowSubmissionModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [schoolyearTypeDropdownOpen, setSchoolyearTypeDropdownOpen] = useState(false);
    const [schoolyearDropdownOpen, setSchoolyearDropdownOpen] = useState(false);
    const schoolyearTypeDropdownRef = useRef<HTMLDivElement>(null);
    const schoolyearDropdownRef = useRef<HTMLDivElement>(null);
    // default export date to today's local date in YYYY-MM-DD
    const localToday = (() => {
        const d = new Date();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${d.getFullYear()}-${mm}-${dd}`;
    })();
    const [exportDate, setExportDate] = useState(localToday); // YYYY-MM-DD
    const [deleting, setDeleting] = useState(false);
    const [showVPConfirmation, setShowVPConfirmation] = useState(false);
    const [showCPConfirmation, setShowCPConfirmation] = useState(false);
    const [submissionData, setSubmissionData] = useState({
        reduction: '',
        odovzdalVP: false,
        odovzdalCP: false,
        odovzdalRO: false,
        submitionDate: '',
        status: 'DRAFT' as 'DRAFT' | 'GENERATED' | 'REJECTED' | 'CANCELLED' | 'ACCEPTED'
    });

    const statusLabels = {
        DRAFT: 'Koncept',
        GENERATED: 'Vygenerovaná',
        REJECTED: 'Zamietnutá',
        CANCELLED: 'Zrušená',
        ACCEPTED: 'Prijatá'
    };

    const statusColors = {
        DRAFT: 'bg-gray-100 text-gray-800',
        GENERATED: 'bg-blue-100 text-blue-800',
        REJECTED: 'bg-red-100 text-red-800',
        CANCELLED: 'bg-orange-100 text-orange-800',
        ACCEPTED: 'bg-green-100 text-green-800'
    };

    const calculateTotalPrice = (kruzky: Kruzok[]) => {
        return kruzky.reduce((total, kruzok) => total + kruzok.price, 0);
    };

    // Calculate price using backend API for consistency
    const calculateCorrectPrice = useCallback(async (prihlaska: Prihlaska): Promise<number> => {
        try {
            const response = await authenticatedFetch(`${process.env.REACT_APP_PATH}/api/admin/calculate-price`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    personId: prihlaska.person.id,
                    kruzkyIds: prihlaska.kruzky.map(k => k.id),
                    vzdelavacyPoukaz: prihlaska.isVP || false,
                    cestnePrehlasenie: prihlaska.isCp || false,
                    prihlaskaId: prihlaska.id  // Include prihlaska ID to get admin reduction
                })
            });

            if (response.ok) {
                const data = await response.json();
                return data.data.totalPrice;
            } else {
                // Fallback to stored toPay value or simple calculation
                return prihlaska.toPay || calculateTotalPrice(prihlaska.kruzky);
            }
        } catch (error) {
            console.error('Error calculating price:', error);
            // Fallback to stored toPay value or simple calculation
            return prihlaska.toPay || calculateTotalPrice(prihlaska.kruzky);
        }
    }, []);

    // Calculate prices for all prihlasky
    const calculateAllPrices = useCallback(async (prihlasky: Prihlaska[]) => {
        const prices: Record<string, number> = {};

        // Calculate prices in batches to avoid overwhelming the API
        for (let i = 0; i < prihlasky.length; i += 5) {
            const batch = prihlasky.slice(i, i + 5);
            const batchPromises = batch.map(async (prihlaska) => {
                const price = await calculateCorrectPrice(prihlaska);
                prices[prihlaska.id] = price;
            });

            await Promise.all(batchPromises);
        }

        setCalculatedPrices(prices);
    }, [calculateCorrectPrice]);

    // Get the display price for a prihlaska
    // Helper function to calculate final price with admin reduction
    const calculateFinalPrice = useCallback((prihlaska: Prihlaska): number => {
        const calculatedPrice = calculatedPrices[prihlaska.id];
        const basePrice = calculatedPrice || calculateTotalPrice(prihlaska.kruzky);
        return basePrice
    }, [calculatedPrices]);

    const getDisplayPrice = (prihlaska: Prihlaska): string => {
        const finalPrice = calculateFinalPrice(prihlaska);
        return `${finalPrice}€`;
    };

    const fetchPrihlasky = useCallback(async () => {
        try {
            setLoading(true);
            // Fetch all data without filters (except maybe a reasonable limit)
            const response = await authenticatedFetch(
                `${process.env.REACT_APP_PATH}/api/admin/prihlasky?limit=2000` // Get all data
            );

            if (!response.ok) {
                throw new Error('Failed to fetch prihlasky');
            }

            const data: PrihlaskyResponse = await response.json();
            const sortedData = data.data.sort((a, b) => a.person.lastName.localeCompare(b.person.lastName));
            setAllPrihlasky(sortedData);

            // Re-enabled automatic price calculation with admin endpoint
            calculateAllPrices(sortedData);
        } catch (err) {
            setError('Chyba pri načítavaní prihlášok');
            console.error('Error fetching prihlasky:', err);
        } finally {
            setLoading(false);
        }
    }, [calculateAllPrices]);

    const fetchAcceptedTotals = useCallback(async () => {
        try {
            const res = await authenticatedFetch(`${process.env.REACT_APP_PATH}/api/admin/prihlasky/totals`);
            if (!res.ok) throw new Error('Failed to fetch totals');
            const json = await res.json();
            setAcceptedTotals(json.data);
        } catch (err) {
            console.error('Error fetching accepted totals:', err);
        }
    }, []);

    const fetchSchoolYears = useCallback(async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_PATH}/api/vseob/rocnik`);
            if (response.ok) {
                const data = await response.json();
                const sortedSchoolYears = data.data ?
                    data.data.sort((a: any, b: any) => (a.order || 0) - (b.order || 0)) :
                    data.sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
                setSchoolYears(sortedSchoolYears);
            }
        } catch (error) {
            console.error('Failed to fetch school years:', error);
        }
    }, []);

    useEffect(() => {
        fetchPrihlasky();
        fetchAcceptedTotals();
        fetchSchoolYears();
    }, [fetchPrihlasky, fetchAcceptedTotals, fetchSchoolYears]);

    useEffect(() => {
        if (!requestedPrihlaskaId) return;
        let cancelled = false;

        const openRequestedPrihlaska = async () => {
            try {
                const response = await authenticatedFetch(
                    `${process.env.REACT_APP_PATH}/api/admin/prihlasky?id=${encodeURIComponent(requestedPrihlaskaId)}&limit=1`
                );
                if (!response.ok) throw new Error('Failed to fetch requested prihlaska');
                const result: PrihlaskyResponse = await response.json();
                const prihlaska = result.data.find(item => item.id === requestedPrihlaskaId);
                if (!prihlaska) throw new Error('Requested prihlaska not found');
                if (cancelled) return;

                setSelectedPrihlaska(prihlaska);
                setShowEditModal(true);
                setSearchParams(params => {
                    params.delete('prihlaskaId');
                    return params;
                }, { replace: true });
            } catch (err) {
                if (cancelled) return;
                console.error('Error opening requested prihlaska:', err);
                setError('Chyba pri otváraní prihlášky');
            }
        };

        openRequestedPrihlaska();
        return () => { cancelled = true; };
    }, [requestedPrihlaskaId, setSearchParams]);

    // Handle clicks outside dropdowns to close them
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (schoolyearTypeDropdownRef.current && !schoolyearTypeDropdownRef.current.contains(event.target as Node)) {
                setSchoolyearTypeDropdownOpen(false);
            }
            if (schoolyearDropdownRef.current && !schoolyearDropdownRef.current.contains(event.target as Node)) {
                setSchoolyearDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Calculate prices when prihlasky data changes - RE-ENABLED WITH ADMIN ENDPOINT
    useEffect(() => {
        if (allPrihlasky.length > 0) {
            calculateAllPrices(allPrihlasky);
        }
    }, [allPrihlasky, calculateAllPrices]);

    const isOlderThan7Days = (dateString: string) => {
        const createdDate = new Date(dateString);

        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return createdDate < sevenDaysAgo;
    };

    // Client-side filtering with useMemo to prevent unnecessary re-renders
    const filteredPrihlasky = useMemo(() => {
        let filtered = [...allPrihlasky];

        // Filter by status
        if (filters.status) {
            filtered = filtered.filter(prihlaska => prihlaska.status === filters.status);
        }

        // Filter by search term
        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            filtered = filtered.filter(prihlaska =>
                prihlaska.person.firstName.toLowerCase().includes(searchLower) ||
                prihlaska.person.lastName.toLowerCase().includes(searchLower) ||
                prihlaska.person.email.toLowerCase().includes(searchLower) ||
                (prihlaska.person.city && prihlaska.person.city.toLowerCase().includes(searchLower)) ||
                prihlaska.hash.toLowerCase().includes(searchLower)
            );
        }

        // Filter by person type
        if (filters.personType) {
            filtered = filtered.filter(prihlaska => prihlaska.person.type === filters.personType);
        }

        // Filter by payment option
        if (filters.paymentOption) {
            filtered = filtered.filter(prihlaska => prihlaska.paymentOption === filters.paymentOption);
        }

        // Filter by schoolyear type
        if (filters.schoolyearType.length > 0) {
            filtered = filtered.filter(prihlaska =>
                prihlaska.person.schoolyear && filters.schoolyearType.includes(prihlaska.person.schoolyear.type)
            );
        }

        // Filter by VP status
        if (filters.hasVP) {
            const hasVP = filters.hasVP === 'true';
            filtered = filtered.filter(prihlaska => prihlaska.isVP === hasVP);
        }

        // Filter by CP status
        if (filters.hasCP) {
            const hasCP = filters.hasCP === 'true';
            filtered = filtered.filter(prihlaska => prihlaska.isCp === hasCP);
        }

        // Filter by submitted VP
        if (filters.submittedVP) {
            const submittedVP = filters.submittedVP === 'true';
            filtered = filtered.filter(prihlaska => {
                // University students are considered as having VP submitted
                if (prihlaska.person.schoolyear?.type === 'UNIVERSITY') {
                    return submittedVP === true;
                }
                return prihlaska.adminsubmition.length > 0 &&
                    prihlaska.adminsubmition[0].odovzdalVP === submittedVP;
            });
        }

        // Filter by submitted CP
        if (filters.submittedCP) {
            const submittedCP = filters.submittedCP === 'true';
            filtered = filtered.filter(prihlaska =>
                prihlaska.adminsubmition.length > 0 &&
                prihlaska.adminsubmition[0].odovzdalCP === submittedCP
            );
        }

        // Filter by submitted RO
        if (filters.submittedRO) {
            const submittedRO = filters.submittedRO === 'true';
            filtered = filtered.filter(prihlaska =>
                prihlaska.adminsubmition.length > 0 &&
                prihlaska.adminsubmition[0].odovzdalRO === submittedRO
            );
        }

        // Filter by kruzok type
        if (filters.kruzokType) {
            filtered = filtered.filter(prihlaska =>
                prihlaska.kruzky.some(kruzok => kruzok.type === filters.kruzokType)
            );
        }

        // Filter by gender
        if (filters.gender) {
            filtered = filtered.filter(prihlaska =>
                prihlaska.person.gender === filters.gender
            );
        }

        // Filter by schoolyear
        if (filters.schoolyearId.length > 0) {
            filtered = filtered.filter(prihlaska =>
                prihlaska.person.schoolyearId && filters.schoolyearId.includes(prihlaska.person.schoolyearId)
            );
        }

        // Filter by date range
        if (filters.dateFrom || filters.dateTo) {
            filtered = filtered.filter(prihlaska => {
                const createdDate = new Date(prihlaska.createdAt);
                const fromDate = filters.dateFrom ? new Date(filters.dateFrom) : null;
                const toDate = filters.dateTo ? new Date(filters.dateTo) : null;

                if (fromDate && createdDate < fromDate) return false;
                if (toDate && createdDate > toDate) return false;
                return true;
            });
        }

        // Filter by submission date range
        if (filters.submissionDateFrom || filters.submissionDateTo) {
            filtered = filtered.filter(prihlaska => {
                if (!prihlaska.adminsubmition.length || !prihlaska.adminsubmition[0].submitionDate) {
                    return false;
                }

                const submissionDate = new Date(prihlaska.adminsubmition[0].submitionDate);
                const fromDate = filters.submissionDateFrom ? new Date(filters.submissionDateFrom) : null;
                const toDate = filters.submissionDateTo ? new Date(filters.submissionDateTo) : null;

                if (fromDate && submissionDate < fromDate) return false;
                if (toDate && submissionDate > toDate) return false;
                return true;
            });
        }

        // Filter by price range
        if (filters.priceFrom || filters.priceTo) {
            filtered = filtered.filter(prihlaska => {
                const finalPrice = calculateFinalPrice(prihlaska);
                const priceFrom = filters.priceFrom ? parseFloat(filters.priceFrom) : null;
                const priceTo = filters.priceTo ? parseFloat(filters.priceTo) : null;

                if (priceFrom && finalPrice < priceFrom) return false;
                if (priceTo && finalPrice > priceTo) return false;
                return true;
            });
        }

        // Filter by old prihlasky (older than 7 days and status is GENERATED)
        if (filters.showOldOnly) {
            filtered = filtered.filter(prihlaska =>
                isOlderThan7Days(prihlaska.generatedAt) && prihlaska.status === 'GENERATED'
            );
        }

        return filtered;
    }, [allPrihlasky, filters, calculateFinalPrice]);

    // Calculate total pages based on filtered results
    const totalPages = useMemo(() => {
        return Math.ceil(filteredPrihlasky.length / filters.limit);
    }, [filteredPrihlasky.length, filters.limit]);

    // Get paginated results for display
    const getPaginatedPrihlasky = useMemo(() => {
        const startIndex = (filters.page - 1) * filters.limit;
        const endIndex = startIndex + filters.limit;
        return filteredPrihlasky.slice(startIndex, endIndex);
    }, [filteredPrihlasky, filters.page, filters.limit]);

    const handleSearchChange = (searchValue: string) => {
        setFilters(prev => ({ ...prev, search: searchValue, page: 1 }));
    };

    const handleFilterChange = (key: string, value: any) => {
        setFilters(prev => {
            // When changing page itself, don't reset it back to 1.
            if (key === 'page') {
                return { ...prev, [key]: value };
            }

            // For all other filter changes, reset to first page.
            return { ...prev, [key]: value, page: 1 };
        });
    };

    const clearAllFilters = () => {
        setFilters({
            status: '',
            search: '',
            page: 1,
            limit: 20,
            showOldOnly: false,
            personType: '',
            paymentOption: '',
            schoolyearType: [] as string[],
            hasVP: '',
            hasCP: '',
            submittedVP: '',
            submittedCP: '',
            submittedRO: '',
            kruzokType: '',
            gender: '',
            schoolyearId: [] as string[],
            dateFrom: '',
            dateTo: '',
            submissionDateFrom: '',
            submissionDateTo: '',
            priceFrom: '',
            priceTo: ''
        });
    };

    const handleSubmissionUpdate = async () => {
        if (!selectedPrihlaska) return;

        try {
            // Update admin submission data
            const submissionResponse = await authenticatedFetch(
                `${process.env.REACT_APP_PATH}/api/admin/prihlaska/${selectedPrihlaska.id}/submission`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        reduction: submissionData.reduction ? parseFloat(submissionData.reduction) : null,
                        odovzdalVP: submissionData.odovzdalVP,
                        odovzdalCP: submissionData.odovzdalCP,
                        odovzdalRO: submissionData.odovzdalRO,
                        submitionDate: submissionData.submitionDate ? new Date(submissionData.submitionDate).toISOString() : null
                    }),
                }
            );

            if (!submissionResponse.ok) {
                throw new Error('Failed to update admin submission');
            }

            // Update status if it changed
            if (submissionData.status !== selectedPrihlaska.status) {
                const statusResponse = await authenticatedFetch(
                    `${process.env.REACT_APP_PATH}/api/admin/prihlaska/${selectedPrihlaska.id}`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            status: submissionData.status,
                            isVP: selectedPrihlaska.isVP,
                            isCp: selectedPrihlaska.isCp,
                            toPay: selectedPrihlaska.toPay
                        }),
                    }
                );

                if (!statusResponse.ok) {
                    throw new Error('Failed to update prihlaska status');
                }
            }

            setShowSubmissionModal(false);
            setSelectedPrihlaska(null);
            setSubmissionData({ reduction: '', odovzdalVP: false, odovzdalCP: false, odovzdalRO: false, submitionDate: '', status: 'DRAFT' });
            fetchPrihlasky();
        } catch (err) {
            setError('Chyba pri aktualizácii admin submission');
            console.error('Error updating admin submission:', err);
        }
    };

    const handleDeletePrihlaska = async () => {
        if (!selectedPrihlaska) return;

        const confirm = window.confirm('Naozaj chcete vymazať túto prihlášku? Táto akcia je nevratná.');
        if (!confirm) return;

        try {
            setDeleting(true);
            const response = await authenticatedFetch(
                `${process.env.REACT_APP_PATH}/api/admin/prihlaska/${selectedPrihlaska.id}`,
                {
                    method: 'DELETE'
                }
            );

            if (!response.ok) {
                throw new Error('Failed to delete prihlaska');
            }

            // Close modal and refresh list
            setShowSubmissionModal(false);
            setSelectedPrihlaska(null);
            fetchPrihlasky();
        } catch (err) {
            setError('Chyba pri mazaní prihlášky');
            console.error('Error deleting prihlaska:', err);
        } finally {
            setDeleting(false);
        }
    };

    const openSubmissionModal = (prihlaska: Prihlaska) => {
        setSelectedPrihlaska(prihlaska);

        // Prefill submission data
        const submission = prihlaska.adminsubmition[0];
        setSubmissionData({
            reduction: submission?.reduction?.toString() || '',
            odovzdalVP: submission?.odovzdalVP || false,
            odovzdalCP: submission?.odovzdalCP || false,
            odovzdalRO: submission?.odovzdalRO || false,
            submitionDate: submission?.submitionDate ? new Date(submission.submitionDate).toISOString().split('T')[0] : '',
            status: prihlaska.status
        });

        setShowSubmissionModal(true);
    };

    const openEditModal = (prihlaska: Prihlaska) => {
        setSelectedPrihlaska(prihlaska);
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        setSelectedPrihlaska(null);
    };

    const handleVPToggle = () => {
        if (!selectedPrihlaska) return;

        // If trying to mark as submitted but person doesn't require VP
        if (!submissionData.odovzdalVP && !selectedPrihlaska.isVP && selectedPrihlaska.person.schoolyear?.type !== 'UNIVERSITY') {
            setShowVPConfirmation(true);
        } else {
            setSubmissionData({ ...submissionData, odovzdalVP: !submissionData.odovzdalVP });
        }
    };

    const handleCPToggle = () => {
        if (!selectedPrihlaska) return;

        // If trying to mark as submitted but person doesn't require CP
        if (!submissionData.odovzdalCP && !selectedPrihlaska.isCp) {
            setShowCPConfirmation(true);
        } else {
            setSubmissionData({ ...submissionData, odovzdalCP: !submissionData.odovzdalCP });
        }
    };

    const confirmVPChange = () => {
        setSubmissionData({ ...submissionData, odovzdalVP: !submissionData.odovzdalVP });
        setShowVPConfirmation(false);
    };

    const confirmCPChange = () => {
        setSubmissionData({ ...submissionData, odovzdalCP: !submissionData.odovzdalCP });
        setShowCPConfirmation(false);
    };

    const handleEditSuccess = () => {
        // Refresh the data
        fetchPrihlasky();
        closeEditModal();
    };

    const handleDownloadPrihlaska = async (prihlaska: Prihlaska) => {
        try {
            const response = await authenticatedFetch(
                `${process.env.REACT_APP_PATH}/api/admin/prihlaska/${prihlaska.id}/download`,
                {
                    method: 'GET',
                }
            );

            if (!response.ok) {
                throw new Error('Failed to download prihlaska');
            }

            // Get the blob from the response
            const blob = await response.blob();

            // Get filename from response headers
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = `${prihlaska.person.lastName}_${prihlaska.person.firstName}_prihlaskaSCVC.pdf`;

            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            // Create a download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError('Chyba pri sťahovaní prihlášky');
            console.error('Error downloading prihlaska:', err);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('sk-SK', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };



    // Export handler used by modal
    const performExport = async (exportType: 'all' | 'filtered' | 'city' | 'ksk' | 'obce' | 'date' = 'all') => {
        try {
            let url = `${process.env.REACT_APP_PATH}/api/admin/prihlasky/export`;
            let filename = 'prihlasky_export';

            if (exportType === 'filtered' || exportType === 'city' || exportType === 'ksk' || exportType === 'obce') {
                // Export filtered list - send IDs of all filtered prihlasky
                const filteredIds = filteredPrihlasky.map(p => p.id);

                if (exportType === 'city' || exportType === 'ksk' || exportType === 'obce') {
                    // For simplified exports, we'll export all eligible records from database
                    // No need to validate here since backend will handle the filtering
                    // Just proceed with the export
                } else if (filteredIds.length === 0) {
                    setError('Žiadne prihlášky na export v aktuálnom filtri.');
                    setShowExportModal(false);
                    return;
                }

                const resp = await authenticatedFetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(exportType === 'city' || exportType === 'ksk' || exportType === 'obce' ? {
                        exportType
                    } : {
                        ids: filteredIds,
                        exportType: 'filtered'
                    })
                });

                if (!resp.ok) throw new Error('Export failed');

                const blob = await resp.blob();
                const disposition = resp.headers.get('Content-Disposition');

                filename = exportType === 'city'
                    ? 'mestsky_export.xlsx'
                    : exportType === 'ksk'
                        ? 'ksk_export.xlsx'
                        : exportType === 'obce'
                            ? 'obce_export.pdf'
                            : `prihlasky_export_filtered_${filteredIds.length}_items.xlsx`;

                if (disposition) {
                    const m = disposition.match(/filename="?([^";]+)"?/);
                    if (m) filename = m[1];
                }

                const link = document.createElement('a');
                const urlObj = window.URL.createObjectURL(blob);
                link.href = urlObj;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(urlObj);
            } else {
                // Original export logic for all or by date
                if (exportType === 'date' && exportDate) {
                    // export for the selected single day -> use from=YYYY-MM-DD and to=YYYY-MM-DD
                    const from = encodeURIComponent(exportDate);
                    const toDate = new Date(exportDate);
                    toDate.setHours(23, 59, 59, 999);
                    const to = encodeURIComponent(toDate.toISOString());
                    url += `?from=${from}&to=${to}`;
                    filename = `prihlasky_export_${exportDate}.xlsx`;
                } else {
                    filename = 'prihlasky_export_all.xlsx';
                }

                const resp = await authenticatedFetch(url, { method: 'GET' });
                if (!resp.ok) throw new Error('Export failed');

                const blob = await resp.blob();
                const disposition = resp.headers.get('Content-Disposition');

                if (disposition) {
                    const m = disposition.match(/filename="?([^";]+)"?/);
                    if (m) filename = m[1];
                }

                const link = document.createElement('a');
                const urlObj = window.URL.createObjectURL(blob);
                link.href = urlObj;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(urlObj);
            }

            setShowExportModal(false);
        } catch (err) {
            console.error('Error exporting prihlasky', err);
            setError('Chyba pri exporte prihlášok');
            setShowExportModal(false);
        }
    };



    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 w-[95vw] absolute left-[2.5vw]">
            {/* Export modal */}
            {showExportModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
                    <div className="relative bg-white rounded-lg p-6 w-full max-w-xl shadow-xl border border-gray-200">
                        <button
                            onClick={() => setShowExportModal(false)}
                            aria-label="Zavrieť"
                            className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                        >
                            ✕
                        </button>

                        <div className="mb-4">
                            <h3 className="text-xl font-bold text-gray-900 flex items-center">
                                <IconDownload className="mr-2 h-6 w-6 text-primary" />
                                Export prihlášok
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">Vyberte typ exportu kliknutím na príslušné tlačidlo</p>
                        </div>

                        <div className="space-y-6">
                            {/* Full Export Section */}
                            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                                <div className="flex items-center mb-3">
                                    <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                                    <h4 className="text-base font-semibold text-blue-900">Kompletné exporty</h4>
                                </div>
                                <p className="text-xs text-blue-700 mb-4">Obsahujú všetky dostupné údaje vrátane kontaktných informácií rodičov</p>

                                <div className="space-y-3">
                                    <div className="flex items-center justify-between p-3 bg-white rounded-md border border-blue-200 hover:border-blue-300 transition-colors">
                                        <div className="text-sm flex-1">
                                            <div className="font-medium text-gray-800">Všetky akceptované prihlášky</div>
                                            <div className="text-xs text-gray-500 mt-1">Exportuje všetky akceptované prihlášky s dátumom odovzdania</div>
                                        </div>
                                        <button
                                            onClick={() => {
                                                performExport('all');
                                            }}
                                            className="ml-3 px-3 py-1.5 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 transition-colors"
                                        >
                                            Exportovať
                                        </button>
                                    </div>

                                    <div className="flex items-center justify-between p-3 bg-white rounded-md border border-blue-200 hover:border-blue-300 transition-colors">
                                        <div className="text-sm flex-1">
                                            <div className="font-medium text-gray-800">Aktuálne filtrovaný zoznam</div>
                                            <div className="text-xs text-gray-500 mt-1">
                                                Exportuje záznamy zo zobrazeného zoznamu ({filteredPrihlasky.length} záznamov)
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => {
                                                performExport('filtered');
                                            }}
                                            disabled={filteredPrihlasky.length === 0}
                                            className={`ml-3 px-3 py-1.5 text-xs rounded-md transition-colors ${filteredPrihlasky.length === 0
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                : 'bg-blue-600 text-white hover:bg-blue-700'
                                                }`}
                                        >
                                            Exportovať
                                        </button>
                                    </div>

                                    <div className="p-3 bg-white rounded-md border border-blue-200">
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="text-sm font-medium text-gray-700">Export podľa dátumu prijatia</label>
                                            <button
                                                onClick={() => {
                                                    performExport('date');
                                                }}
                                                className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 transition-colors"
                                            >
                                                Exportovať
                                            </button>
                                        </div>
                                        <input
                                            type="date"
                                            value={exportDate}
                                            onChange={(e) => setExportDate(e.target.value)}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                        />
                                        <div className="text-xs text-gray-500 mt-2 flex items-center">
                                            Exportuje akceptované prihlášky akceptované v tento deň
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Simplified Export Section */}
                            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                                <div className="flex items-center mb-3">
                                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                                    <h4 className="text-base font-semibold text-green-900">Zjednodušené exporty</h4>
                                </div>
                                <p className="text-xs text-green-700 mb-4">Obsahujú len základné údaje pre špecifické účely</p>

                                <div className="flex items-center justify-between p-3 bg-white rounded-md border border-green-200 hover:border-green-300 transition-colors">
                                    <div className="text-sm flex-1">
                                        <div className="font-medium text-gray-800 flex items-center">
                                            Mestský export (Košice)
                                            <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">5-15 rokov</span>
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                            Základné údaje detí z Košíc vo veku 5-15 rokov pre mestskú správu
                                        </div>
                                        <div className="text-xs text-green-600 mt-1 font-medium">
                                            Obsahuje: Meno, Priezvisko, Dátum narodenia, Adresu, Krúžky
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => {
                                            performExport('city');
                                        }}
                                        className="ml-3 px-3 py-1.5 bg-green-600 text-white text-xs rounded-md hover:bg-green-700 transition-colors"
                                    >
                                        Exportovať
                                    </button>
                                </div>

                                <div className="flex items-center justify-between p-3 bg-white rounded-md border border-green-200 hover:border-green-300 transition-colors mt-3">
                                    <div className="text-sm flex-1">
                                        <div className="font-medium text-gray-800 flex items-center">
                                            KSK export
                                            <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">16-24 rokov</span>
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                            Základné údaje osôb z Košického kraja vo veku 16-24 rokov s odovzdaným čestným prehlásením
                                        </div>
                                        <div className="text-xs text-green-600 mt-1 font-medium">
                                            Nepriradené obce budú na konci exportu farebne označené
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => {
                                            performExport('ksk');
                                        }}
                                        className="ml-3 px-3 py-1.5 bg-green-600 text-white text-xs rounded-md hover:bg-green-700 transition-colors"
                                    >
                                        Exportovať
                                    </button>
                                </div>

                                <div className="flex items-center justify-between p-3 bg-white rounded-md border border-green-200 hover:border-green-300 transition-colors mt-3">
                                    <div className="text-sm flex-1">
                                        <div className="font-medium text-gray-800 flex items-center">
                                            Obce
                                            <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">5-15 rokov</span>
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                            PDF žiadostí pre obce podľa adries obecných úradov
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => performExport('obce')}
                                        className="ml-3 px-3 py-1.5 bg-green-600 text-white text-xs rounded-md hover:bg-green-700 transition-colors"
                                    >
                                        Exportovať
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end mt-6 pt-4 border-t border-gray-200">
                            <button
                                onClick={() => setShowExportModal(false)}
                                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                Zavrieť
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Správa prihlášok</h2>

                {/* Statistics Summary */}
                <div className="mt-6 mb-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {/* Generic stat card */}
                    <div className="flex flex-col justify-between bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                        <div className="text-sm font-medium text-gray-500">Celkom prihlášok</div>
                        <div className="mt-2 text-3xl font-extrabold text-gray-900">{allPrihlasky.length}</div>
                        <div className="mt-2 text-xs text-gray-500">Celkový počet záznamov</div>
                    </div>

                    <div className="flex flex-col justify-between bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                        <div className="text-sm font-medium text-green-600">Akceptované</div>
                        <div className="mt-2 text-2xl font-bold text-green-800">
                            {allPrihlasky.filter(p => p.status === 'ACCEPTED').length}
                            <span className="ml-2 text-sm text-green-800/70">(+{allPrihlasky.filter(p => p.status === 'GENERATED').length})</span>
                        </div>
                        <div className="mt-2 text-2xl font-bold text-green-800"></div>
                        <div className="mt-2 text-xs text-gray-500">Prijaté (Očakávané)</div>
                    </div>

                    <div className="flex flex-col justify-between bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                        <div className="text-sm font-medium text-red-600">Meškajúce</div>
                        <div className="mt-2 text-2xl font-bold text-red-800">{allPrihlasky.filter(p => isOlderThan7Days(p.generatedAt) && p.status === 'GENERATED').length}</div>
                        <div className="mt-2 text-xs text-gray-500">Očakávané 7+ dní</div>
                    </div>

                    {/* VP / CP: show submitted / expected as small caption */}
                    <div className="flex flex-col justify-between bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                        <div className="text-sm font-medium text-teal-600">Vzdelávací poukaz</div>

                        <div className="mt-2 text-2xl font-bold text-teal-800">
                            {allPrihlasky.filter(p => p.isVP && p.adminsubmition && p.adminsubmition.length > 0 && p.adminsubmition[0].odovzdalVP).length}
                            <span className="ml-2 text-sm text-teal-800/70">(+{allPrihlasky.filter(p => p.isVP && p.person.schoolyear?.type !== 'UNIVERSITY' && (p.status === 'GENERATED' || p.status === 'ACCEPTED')).length - allPrihlasky.filter(p => p.isVP && p.adminsubmition && p.person.schoolyear?.type !== 'UNIVERSITY' && p.adminsubmition.length > 0 && p.adminsubmition[0].odovzdalVP && (p.status === 'GENERATED' || p.status === 'ACCEPTED')).length})</span>
                        </div>
                        <div className="mt-2 text-xs text-gray-500">Prijaté (Očakávané)</div>
                    </div>


                    <div className="flex flex-col justify-between bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                        <div className="text-sm font-medium text-emerald-600">Čestné prehlásenie</div>
                        <div className="mt-2 text-2xl font-bold text-emerald-800"></div>
                        <div className="mt-2 text-2xl font-bold text-emerald-800">
                            {allPrihlasky.filter(p => p.isCp && p.adminsubmition && p.adminsubmition.length > 0 && p.adminsubmition[0].odovzdalCP).length}
                            <span className="ml-2 text-sm text-emerald-800/70">(+{allPrihlasky.filter(p => p.isCp && (p.status === 'GENERATED' || p.status === 'ACCEPTED')).length - allPrihlasky.filter(p => p.isCp && p.adminsubmition && p.adminsubmition.length > 0 && p.adminsubmition[0].odovzdalCP && (p.status === 'GENERATED' || p.status === 'ACCEPTED')).length})</span>
                        </div>
                        <div className="mt-2 text-xs text-gray-500">Prijaté (Očakávané)</div>
                    </div>

                    <div className="flex flex-col justify-between bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                        <div className="text-sm font-medium text-gray-600">Financie</div>
                        <div className="mt-2 text-2xl font-bold text-gray-900">
                            {acceptedTotals ? `${acceptedTotals.total.toFixed(0)}€` : '—'}
                        </div>
                        <div className="mt-2 text-xs text-gray-500">
                            Z toho zápisné: {acceptedTotals ? `${acceptedTotals.zapisne.toFixed(0)}€` : '—'}
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="mt-4 space-y-4">
                    {/* Basic Filters Row */}
                    <div className="flex flex-wrap gap-4">
                        <div className="min-w-[200px]">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Status
                            </label>
                            <select
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                            >
                                <option value="">Všetky</option>
                                {Object.entries(statusLabels).map(([value, label]) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="min-w-[250px] flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Hľadať
                            </label>
                            <SearchInput
                                value={filters.search}
                                onChange={handleSearchChange}
                                placeholder="Meno, email, mesto, hash..."
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                debounceMs={300}
                            />
                        </div>

                        <div className="min-w-[150px]">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Typ osoby
                            </label>
                            <select
                                value={filters.personType}
                                onChange={(e) => handleFilterChange('personType', e.target.value)}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                            >
                                <option value="">Všetky</option>
                                <option value="PARENT">Rodič</option>
                                <option value="ADULT">Dospelý</option>
                                <option value="KID">Dieťa</option>
                            </select>
                        </div>

                        <div className="min-w-[150px]">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Platba
                            </label>
                            <select
                                value={filters.paymentOption}
                                onChange={(e) => handleFilterChange('paymentOption', e.target.value)}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                            >
                                <option value="">Všetky</option>
                                <option value="CASH">Hotovosť</option>
                                <option value="EDUPAGE">EduPage</option>
                            </select>
                        </div>

                        <div className="flex items-end space-x-2">
                            <button
                                onClick={() => {
                                    // Open modal to ask whether to export a specific day or all
                                    setExportDate(localToday);
                                    setShowExportModal(true);
                                }}
                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark"
                            >
                                Exportovať XLSX
                            </button>
                        </div>
                    </div>

                    {/* Advanced Filters Toggle */}
                    <div className="flex items-center justify-between border-t pt-4">
                        <button
                            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                            className="flex items-center text-sm text-primary hover:text-primary-dark font-medium"
                        >
                            <span>{showAdvancedFilters ? 'Skryť' : 'Zobraziť'} pokročilé filtre</span>
                            <span className={`ml-1 transform transition-transform duration-200 ${showAdvancedFilters ? 'rotate-180' : ''}`}>
                                ▼
                            </span>
                        </button>

                        <div className="flex items-center space-x-3">
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="showOldOnly"
                                    checked={filters.showOldOnly}
                                    onChange={(e) => handleFilterChange('showOldOnly', e.target.checked)}
                                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                                />
                                <label htmlFor="showOldOnly" className="ml-2 text-sm text-gray-700">
                                    Len meškajúce prihlášky (7+ dní)
                                </label>
                            </div>

                            <button
                                onClick={clearAllFilters}
                                className="text-sm text-gray-500 hover:text-gray-700 font-medium"
                            >
                                Vymazať filtre
                            </button>
                        </div>
                    </div>

                    {/* Advanced Filters Panel */}
                    {showAdvancedFilters && (
                        <div className="border rounded-lg p-4 bg-gray-50 space-y-4">
                            {/* Row 1: VP/CP Filters */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Očakávaný VP
                                    </label>
                                    <select
                                        value={filters.hasVP}
                                        onChange={(e) => handleFilterChange('hasVP', e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                    >
                                        <option value="">Všetky</option>
                                        <option value="true">Áno</option>
                                        <option value="false">Nie</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Očakávané CP
                                    </label>
                                    <select
                                        value={filters.hasCP}
                                        onChange={(e) => handleFilterChange('hasCP', e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                    >
                                        <option value="">Všetky</option>
                                        <option value="true">Áno</option>
                                        <option value="false">Nie</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Odovzdal VP
                                    </label>
                                    <select
                                        value={filters.submittedVP}
                                        onChange={(e) => handleFilterChange('submittedVP', e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                    >
                                        <option value="">Všetky</option>
                                        <option value="true">Áno</option>
                                        <option value="false">Nie</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Odovzdal CP
                                    </label>
                                    <select
                                        value={filters.submittedCP}
                                        onChange={(e) => handleFilterChange('submittedCP', e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                    >
                                        <option value="">Všetky</option>
                                        <option value="true">Áno</option>
                                        <option value="false">Nie</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Odovzdal rozhodnutie
                                    </label>
                                    <select
                                        value={filters.submittedRO}
                                        onChange={(e) => handleFilterChange('submittedRO', e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                    >
                                        <option value="">Všetky</option>
                                        <option value="true">Áno</option>
                                        <option value="false">Nie</option>
                                    </select>
                                </div>
                            </div>

                            {/* Row 2: Misc Filters */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Typ krúžku
                                    </label>
                                    <select
                                        value={filters.kruzokType}
                                        onChange={(e) => handleFilterChange('kruzokType', e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                    >
                                        <option value="">Všetky</option>
                                        <option value="TRIH">Tri hôrky</option>
                                        <option value="BERN">Bernolákova</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Pohlavie
                                    </label>
                                    <select
                                        value={filters.gender}
                                        onChange={(e) => handleFilterChange('gender', e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                    >
                                        <option value="">Všetky</option>
                                        <option value="MALE">Muž</option>
                                        <option value="FEMALE">Žena</option>
                                    </select>
                                </div>

                                <div className="relative" ref={schoolyearTypeDropdownRef}>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Typ ročníka
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => setSchoolyearTypeDropdownOpen(!schoolyearTypeDropdownOpen)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary bg-white text-left flex items-center justify-between"
                                    >
                                        <span className="truncate">
                                            {filters.schoolyearType.length === 0 ? 'Všetky' :
                                                filters.schoolyearType.length === 1 ?
                                                    ({ 'PRESCHOOL': 'Materská škola', 'PRIMARY': 'Základná škola', 'HIGHSCHOOL': 'Stredná škola', 'UNIVERSITY': 'VŠ a mladý pracujúci' }[filters.schoolyearType[0]] || filters.schoolyearType[0]) :
                                                    `${filters.schoolyearType.length} vybraných`}
                                        </span>
                                        <span className={`transform transition-transform duration-200 ${schoolyearTypeDropdownOpen ? 'rotate-180' : ''}`}>
                                            ▼
                                        </span>
                                    </button>
                                    {schoolyearTypeDropdownOpen && (
                                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                                            <div className="py-1">
                                                {[
                                                    { value: 'PRESCHOOL', label: 'Materská škola' },
                                                    { value: 'PRIMARY', label: 'Základná škola' },
                                                    { value: 'HIGHSCHOOL', label: 'Stredná škola' },
                                                    { value: 'UNIVERSITY', label: 'VŠ a mladý pracujúci' }
                                                ].map((option) => (
                                                    <label key={option.value} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={filters.schoolyearType.includes(option.value)}
                                                            onChange={(e) => {
                                                                const newValues = e.target.checked
                                                                    ? [...filters.schoolyearType, option.value]
                                                                    : filters.schoolyearType.filter(v => v !== option.value);
                                                                handleFilterChange('schoolyearType', newValues);
                                                            }}
                                                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                                                        />
                                                        <span className="ml-2 text-sm text-gray-900">{option.label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="relative" ref={schoolyearDropdownRef}>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Ročník
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => setSchoolyearDropdownOpen(!schoolyearDropdownOpen)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary bg-white text-left flex items-center justify-between"
                                    >
                                        <span className="truncate">
                                            {filters.schoolyearId.length === 0 ? 'Všetky' :
                                                filters.schoolyearId.length === 1 ?
                                                    (schoolYears.find(sy => sy.id === filters.schoolyearId[0])?.name || filters.schoolyearId[0]) :
                                                    `${filters.schoolyearId.length} vybraných`}
                                        </span>
                                        <span className={`transform transition-transform duration-200 ${schoolyearDropdownOpen ? 'rotate-180' : ''}`}>
                                            ▼
                                        </span>
                                    </button>
                                    {schoolyearDropdownOpen && (
                                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                            <div className="py-1">
                                                {schoolYears.map((schoolYear) => (
                                                    <label key={schoolYear.id} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={filters.schoolyearId.includes(schoolYear.id)}
                                                            onChange={(e) => {
                                                                const newValues = e.target.checked
                                                                    ? [...filters.schoolyearId, schoolYear.id]
                                                                    : filters.schoolyearId.filter(v => v !== schoolYear.id);
                                                                handleFilterChange('schoolyearId', newValues);
                                                            }}
                                                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                                                        />
                                                        <span className="ml-2 text-sm text-gray-900">{schoolYear.name}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Cena od (€)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={filters.priceFrom}
                                        onChange={(e) => handleFilterChange('priceFrom', e.target.value)}
                                        placeholder="0.00"
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Cena do (€)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={filters.priceTo}
                                        onChange={(e) => handleFilterChange('priceTo', e.target.value)}
                                        placeholder="999.99"
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                    />
                                </div>
                            </div>

                            {/* Row 3: Date Filters */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Vytvorené od
                                    </label>
                                    <input
                                        type="date"
                                        value={filters.dateFrom}
                                        onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Vytvorené do
                                    </label>
                                    <input
                                        type="date"
                                        value={filters.dateTo}
                                        onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Odovzdané od
                                    </label>
                                    <input
                                        type="date"
                                        value={filters.submissionDateFrom}
                                        onChange={(e) => handleFilterChange('submissionDateFrom', e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Odovzdané do
                                    </label>
                                    <input
                                        type="date"
                                        value={filters.submissionDateTo}
                                        onChange={(e) => handleFilterChange('submissionDateTo', e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                    />
                                </div>
                            </div>

                        </div>
                    )}

                    {/* Filter Summary */}
                    {(filters.status || filters.search || filters.personType || filters.paymentOption ||
                        filters.hasVP || filters.hasCP || filters.submittedVP || filters.submittedCP || filters.submittedRO ||
                        filters.kruzokType || filters.gender || filters.schoolyearType.length > 0 || filters.schoolyearId.length > 0 ||
                        filters.dateFrom || filters.dateTo || filters.submissionDateFrom || filters.submissionDateTo ||
                        filters.priceFrom || filters.priceTo || filters.showOldOnly) && (
                            <div className="flex items-center justify-between text-sm bg-blue-50 border border-blue-200 rounded-md p-3">
                                <span className="text-blue-700">
                                    Zobrazuje sa {filteredPrihlasky.length} z {allPrihlasky.length} prihlášok
                                </span>
                            </div>
                        )}
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="p-4 bg-red-50 border-l-4 border-red-400">
                    <p className="text-red-700">{error}</p>
                </div>
            )}

            {/* Legend */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center text-sm text-gray-600">

                    {filters.showOldOnly && (
                        <div className="ml-4 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-md text-xs font-medium">
                            Zobrazujú sa len meškajúce prihlášky
                        </div>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-2 sm:px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Osoba
                                </th>
                                <th className="px-2 sm:px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell w-[16ch]">
                                    Hash
                                </th>
                                <th className="px-2 sm:px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-2 sm:px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Krúžky
                                </th>
                                <th className="px-2 sm:px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                                    Suma
                                </th>
                                <th className="px-2 sm:px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                                    Vytvorené
                                </th>
                                <th className="px-2 sm:px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                                    VP/CP
                                </th>
                                <th className="px-2 sm:px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">

                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {getPaginatedPrihlasky.map((prihlaska) => (
                                <tr key={prihlaska.id} className={`hover:bg-gray-50 ${isOlderThan7Days(prihlaska.generatedAt) && prihlaska.status === 'GENERATED' ? 'bg-yellow-50 border-l-4 border-l-yellow-400' : ''}`}>
                                    <td className="px-2 sm:px-3 py-4 whitespace-nowrap">
                                        <div>
                                            <div className="text-sm font-medium text-gray-900">
                                                {prihlaska.person.lastName} {prihlaska.person.firstName}
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                {prihlaska.person.email}
                                            </div>
                                            {prihlaska.person.city && (
                                                <div className="text-xs text-gray-400">
                                                    {prihlaska.person.city}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-2 sm:px-3 py-4 whitespace-nowrap hidden sm:table-cell w-[16ch]">
                                        <div className="text-xs font-mono text-gray-600 ">
                                            <div className="" title={prihlaska.hash}>
                                                {prihlaska.hash.slice(0, 16)}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-2 sm:px-3 py-4 whitespace-nowrap">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[prihlaska.status]}`}>
                                            {statusLabels[prihlaska.status]}
                                        </span>
                                    </td>
                                    <td className="px-2 sm:px-3 py-4">
                                        <div className="text-sm">
                                            {prihlaska.kruzky.map((kruzok, index) => (
                                                <div key={kruzok.id} className="mb-1">
                                                    <span className="font-medium">{kruzok.name}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="px-2 sm:px-3 py-4 whitespace-nowrap hidden md:table-cell">
                                        <div className="text-sm font-medium text-gray-900">
                                            {getDisplayPrice(prihlaska)}
                                        </div>
                                        {prihlaska.adminsubmition && prihlaska.adminsubmition.length > 0 && prihlaska.adminsubmition[0].reduction && (
                                            <div className="text-xs text-blue-600">
                                                Zľava: {prihlaska.adminsubmition[0].reduction}€
                                            </div>
                                        )}

                                    </td>
                                    <td className="px-2 sm:px-3 py-4 whitespace-nowrap text-sm text-gray-500 hidden lg:table-cell">
                                        {formatDate(prihlaska.createdAt)}
                                        {prihlaska.adminsubmition && prihlaska.adminsubmition.length > 0 && prihlaska.adminsubmition[0].submitionDate && (
                                            <div className="text-xs text-blue-600">
                                                Odovzdané: {formatDate(prihlaska.adminsubmition[0].submitionDate)}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-2 sm:px-3 py-4 whitespace-nowrap hidden md:table-cell">
                                        <div className="flex space-x-1 sm:space-x-2">
                                            {prihlaska.person.schoolyear?.type !== 'UNIVERSITY' ?
                                                <span
                                                    className={`inline-flex items-center px-1 sm:px-2 py-1 rounded-full text-xs font-medium ${prihlaska.isVP ? (prihlaska.adminsubmition && prihlaska.adminsubmition.length > 0 && prihlaska.adminsubmition[0].odovzdalVP ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800') : (prihlaska.adminsubmition && prihlaska.adminsubmition.length > 0 && prihlaska.adminsubmition[0].odovzdalVP ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800')}`}>

                                                    VP: {
                                                        (prihlaska.adminsubmition && prihlaska.adminsubmition.length > 0) ?
                                                            <>
                                                                {prihlaska.isVP ? (
                                                                    prihlaska.adminsubmition[0].odovzdalVP ? 'Odovzdané' : 'Očakáva sa'
                                                                ) : (prihlaska.adminsubmition[0].odovzdalVP ? 'Odovzdané !!' : 'Nie')}
                                                            </>
                                                            :
                                                            <>
                                                                {prihlaska.isVP ? 'Očakáva sa' : 'Nie'}
                                                            </>
                                                    }
                                                </span> :
                                                <span
                                                    className={`inline-flex items-center px-1 sm:px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800`}>

                                                    VP: Vysokoškolák
                                                </span>

                                            }
                                            <span className={`inline-flex items-center px-1 sm:px-2 py-1 rounded-full text-xs font-medium ${prihlaska.isCp ? (prihlaska.adminsubmition && prihlaska.adminsubmition.length > 0 && prihlaska.adminsubmition[0].odovzdalCP ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800') : (prihlaska.adminsubmition && prihlaska.adminsubmition.length > 0 && prihlaska.adminsubmition[0].odovzdalCP ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800')
                                                }`}>

                                                CP: {
                                                    (prihlaska.adminsubmition && prihlaska.adminsubmition.length > 0) ?
                                                        <>
                                                            {prihlaska.isCp ? (
                                                                prihlaska.adminsubmition[0].odovzdalCP ? 'Odovzdané' : 'Očakáva sa'
                                                            ) : (prihlaska.adminsubmition[0].odovzdalCP ? 'Odovzdané !!' : 'Nie')}
                                                        </>
                                                        :
                                                        <>
                                                            {prihlaska.isCp ? 'Očakáva sa' : 'Nie'}
                                                        </>
                                                }
                                            </span>
                                        </div>

                                    </td>
                                    <td className="px-2 sm:px-3 py-4 whitespace-nowrap text-sm font-medium">
                                        <div className="flex flex-col sm:flex-row gap-2">
                                            <button
                                                onClick={() => openEditModal(prihlaska)}
                                                className="text-blue-600 hover:text-blue-800 p-1"
                                                title="Upraviť prihlášku"
                                            >
                                                <IconFileText />
                                            </button>
                                            <button
                                                onClick={() => openSubmissionModal(prihlaska)}
                                                className="text-primary hover:text-primary-dark p-1"
                                                title="Upraviť admin údaje"
                                            >
                                                <IconSettings />
                                            </button>
                                            <button
                                                onClick={() => handleDownloadPrihlaska(prihlaska)}
                                                className="text-green-600 hover:text-green-800 p-1"
                                                title="Stiahnuť PDF"
                                            >
                                                <IconDownload />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {
                totalPages > 1 && (
                    <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
                        <div className="flex items-center space-x-4 text-sm text-gray-700">
                            <span>Stránka {filters.page} z {totalPages}</span>
                            <span>•</span>
                            <span>Zobrazuje {((filters.page - 1) * filters.limit) + 1}-{Math.min(filters.page * filters.limit, filteredPrihlasky.length)} z {filteredPrihlasky.length}</span>
                            {filteredPrihlasky.length !== allPrihlasky.length && (
                                <>
                                    <span>•</span>
                                    <span className="text-blue-600">Filtrované z {allPrihlasky.length} celkom</span>
                                </>
                            )}
                        </div>
                        <div className="flex items-center space-x-2">
                            <select
                                value={filters.limit}
                                onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                                className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                            >
                                <option value="10">10</option>
                                <option value="20">20</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                            <span className="text-sm text-gray-500">na stránku</span>
                            <div className="flex space-x-1 ml-4">
                                <button
                                    onClick={() => handleFilterChange('page', 1)}
                                    disabled={filters.page === 1}
                                    className="px-2 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                    title="Prvá stránka"
                                >
                                    ⟪
                                </button>
                                <button
                                    onClick={() => handleFilterChange('page', filters.page - 1)}
                                    disabled={filters.page === 1}
                                    className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                >
                                    Predchádzajúca
                                </button>
                                <button
                                    onClick={() => handleFilterChange('page', filters.page + 1)}
                                    disabled={filters.page === totalPages}
                                    className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                >
                                    Ďalšia
                                </button>
                                <button
                                    onClick={() => handleFilterChange('page', totalPages)}
                                    disabled={filters.page === totalPages}
                                    className="px-2 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                    title="Posledná stránka"
                                >
                                    ⟫
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Admin Submission Modal */}
            {
                showSubmissionModal && selectedPrihlaska && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 w-full  max-w-[600px]">
                            <h3 className="text-lg font-semibold mb-4">
                                Admin záznam - {selectedPrihlaska.person.firstName} {selectedPrihlaska.person.lastName}
                            </h3>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Status prihlášky
                                    </label>
                                    <select
                                        value={submissionData.status}
                                        onChange={(e) => setSubmissionData({ ...submissionData, status: e.target.value as any })}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                    >
                                        {Object.entries(statusLabels).map(([value, label]) => (
                                            <option key={value} value={value}>{label}</option>
                                        ))}
                                    </select>
                                </div>



                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Zľava (€)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={submissionData.reduction}
                                        onChange={(e) => setSubmissionData({ ...submissionData, reduction: e.target.value })}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                        placeholder="0.00"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Dátum odovzdania (vyplní sa automaticky pri zmene statusu na "Prijatá")
                                    </label>
                                    <input
                                        type="date"
                                        value={submissionData.submitionDate}
                                        onChange={(e) => setSubmissionData({ ...submissionData, submitionDate: e.target.value })}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                    />
                                </div>

                                <div className="flex items-center">
                                    <div
                                        onClick={handleVPToggle}
                                        className={`flex cursor-pointer justify-center items-center size-8 border rounded ${submissionData.odovzdalVP ? 'bg-primary border-gray-300' :
                                            (!selectedPrihlaska?.isVP && selectedPrihlaska?.person.schoolyear?.type !== 'UNIVERSITY') ?
                                                'border-orange-400 bg-orange-50' : 'border-gray-300'
                                            }`}
                                    >
                                        {submissionData.odovzdalVP ? <IconCheck color="white" /> : null}
                                    </div>
                                    <label htmlFor="odovzdalVP" className={`ml-2 text-sm ${(!selectedPrihlaska?.isVP && selectedPrihlaska?.person.schoolyear?.type !== 'UNIVERSITY') ?
                                        'text-orange-600' : 'text-gray-700'
                                        }`}>
                                        Odovzdal vzdelávací poukaz
                                        {(!selectedPrihlaska?.isVP && selectedPrihlaska?.person.schoolyear?.type !== 'UNIVERSITY') && (
                                            <span className="ml-1 text-xs text-orange-500">(neplánoval)</span>
                                        )}
                                    </label>
                                </div>

                                <div className="flex items-center">
                                    <div
                                        onClick={handleCPToggle}
                                        className={`flex cursor-pointer justify-center items-center size-8 border rounded ${submissionData.odovzdalCP ? 'bg-primary border-gray-300' :
                                            !selectedPrihlaska?.isCp ? 'border-orange-400 bg-orange-50' : 'border-gray-300'
                                            }`}
                                    >
                                        {submissionData.odovzdalCP ? <IconCheck color="white" /> : null}
                                    </div>
                                    <label htmlFor="odovzdalCP" className={`ml-2 text-sm ${!selectedPrihlaska?.isCp ? 'text-orange-600' : 'text-gray-700'
                                        }`}>
                                        Odovzdal čestné prehlásenie
                                        {!selectedPrihlaska?.isCp && (
                                            <span className="ml-1 text-xs text-orange-500">(neplánoval)</span>
                                        )}
                                    </label>
                                </div>

                                <div className="flex items-center">
                                    <div
                                        onClick={() => {
                                            setSubmissionData({ ...submissionData, odovzdalRO: !submissionData.odovzdalRO })
                                        }}
                                        className={`flex cursor-pointer justify-center items-center size-8 border border-gray-300 rounded ${submissionData.odovzdalRO ? 'bg-primary' : ''}`}
                                    >
                                        {submissionData.odovzdalRO ? <IconCheck color="white" /> : null}
                                    </div>
                                    <label htmlFor="odovzdalRO" className="ml-2 text-sm text-gray-700">
                                        Odovzdal rozhodnutie
                                    </label>
                                </div>
                            </div>

                            <div className="mt-6 flex space-x-3">
                                <button
                                    onClick={handleSubmissionUpdate}
                                    className="flex-1 bg-primary text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                                >
                                    Uložiť
                                </button>
                                <button
                                    onClick={() => {
                                        setShowSubmissionModal(false);
                                        setSelectedPrihlaska(null);
                                        setSubmissionData({ reduction: '', odovzdalVP: false, odovzdalCP: false, odovzdalRO: false, submitionDate: '', status: 'DRAFT' });
                                        setShowVPConfirmation(false);
                                        setShowCPConfirmation(false);
                                    }}
                                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                                >
                                    Zrušiť
                                </button>

                                <button
                                    onClick={handleDeletePrihlaska}
                                    disabled={deleting}
                                    className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                                    title="Vymazať prihlášku"
                                >
                                    <IconTrash />
                                    <span>{deleting ? 'Maže sa...' : 'Vymazať'}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* VP Confirmation Modal */}
            {showVPConfirmation && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
                    <div className="bg-white rounded-lg p-6 max-w-md mx-4">
                        <h3 className="text-lg font-semibold mb-4 text-orange-600">
                            Potvrdenie
                        </h3>
                        <p className="text-sm text-gray-700 mb-6">
                            Táto osoba neplánovala odovzdať vzdelávací poukaz, ale chcete označiť že ho odovzdala.
                            Ste si istý?
                        </p>
                        <div className="flex space-x-3">
                            <button
                                onClick={confirmVPChange}
                                className="flex-1 bg-orange-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-orange-700"
                            >
                                Áno, označiť ako odovzdaný
                            </button>
                            <button
                                onClick={() => setShowVPConfirmation(false)}
                                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-400"
                            >
                                Zrušiť
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* CP Confirmation Modal */}
            {showCPConfirmation && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
                    <div className="bg-white rounded-lg p-6 max-w-md mx-4">
                        <h3 className="text-lg font-semibold mb-4 text-orange-600">
                            Potvrdenie
                        </h3>
                        <p className="text-sm text-gray-700 mb-6">
                            Táto osoba neplánovala odovzdať čestné prehlásenie, ale chcete označiť že ho odovzdala.
                            Ste si istý?
                        </p>
                        <div className="flex space-x-3">
                            <button
                                onClick={confirmCPChange}
                                className="flex-1 bg-orange-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-orange-700"
                            >
                                Áno, označiť ako odovzdané
                            </button>
                            <button
                                onClick={() => setShowCPConfirmation(false)}
                                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-400"
                            >
                                Zrušiť
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Admin Edit Prihlaska Modal */}
            {
                showEditModal && selectedPrihlaska && (
                    <AdminEditPrihlaskaModal
                        prihlaska={selectedPrihlaska}
                        isOpen={showEditModal}
                        onClose={closeEditModal}
                        onSuccess={handleEditSuccess}
                    />
                )
            }
        </div>
    );
};

export default Prihlasky;
